import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { useToast } from '../../components/UI/ToastProvider';
import { licenseService } from '../../services/licenseService';
import type { CustomerExperienceConfig } from '../../types/tenantLicense';

interface CustomerExperiencePanelProps {
  customerId: string;
  reloadKey?: number;
}

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })
    ?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

const hasOverrideValue = (value?: string | null): boolean => {
  return Boolean(value?.trim());
};

export const CustomerExperiencePanel: React.FC<CustomerExperiencePanelProps> = ({
  customerId,
  reloadKey = 0,
}) => {
  const { addToast } = useToast();
  const [config, setConfig] = useState<CustomerExperienceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingSystem, setSavingSystem] = useState(false);
  const [savingModuleCode, setSavingModuleCode] = useState<string | null>(null);
  const [clearingModuleCode, setClearingModuleCode] = useState<string | null>(null);
  const [defaultProductModuleId, setDefaultProductModuleId] = useState<string>('');
  const [requiresSystemSelection, setRequiresSystemSelection] = useState(false);
  const [overrideDrafts, setOverrideDrafts] = useState<Record<string, string>>({});

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await licenseService.getCustomerExperienceConfig(customerId);
      setConfig(data);
      setDefaultProductModuleId(data.defaultProductModuleId ?? '');
      setRequiresSystemSelection(data.requiresSystemSelection);
      const drafts: Record<string, string> = {};
      for (const row of data.licensedModules) {
        drafts[row.moduleCode] = row.dashboardUrlOverride ?? '';
      }
      setOverrideDrafts(drafts);
    } catch {
      addToast('Erro ao carregar configuração de licenciamento.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast, customerId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, reloadKey]);

  const canConfigureExperience =
    config != null &&
    (Boolean(config.isLicenseBypass) ||
      (config.hasActiveSubscription && config.licensedModules.length > 0));

  const handleSaveSystemConfig = async () => {
    setSavingSystem(true);
    try {
      await licenseService.updateCustomerSystemConfig(customerId, {
        defaultProductModuleId: defaultProductModuleId || null,
        requiresSystemSelection,
      });
      addToast('Preferências globais salvas.', 'success');
      await loadConfig();
    } catch (error: unknown) {
      addToast(getApiErrorMessage(error, 'Erro ao salvar preferências globais.'), 'error');
    } finally {
      setSavingSystem(false);
    }
  };

  const handleSaveModuleOverride = async (moduleCode: string) => {
    if (!canConfigureExperience)
    {
      addToast(
        config?.isLicenseBypass
          ? 'Nenhum módulo ativo no catálogo para configurar overrides.'
          : 'Ative uma assinatura antes de configurar overrides de dashboard.',
        'error'
      );
      return;
    }

    setSavingModuleCode(moduleCode);
    try {
      const value = overrideDrafts[moduleCode]?.trim() ?? '';
      await licenseService.setProductModuleConfig(customerId, moduleCode, {
        dashboardUrlOverride: value || null,
      });
      addToast(`Override do módulo ${moduleCode} salvo.`, 'success');
      await loadConfig();
    } catch (error: unknown) {
      addToast(getApiErrorMessage(error, 'Erro ao salvar override de dashboard.'), 'error');
    } finally {
      setSavingModuleCode(null);
    }
  };

  const handleClearModuleOverride = async (moduleCode: string) => {
    const row = config?.licensedModules.find((item) => item.moduleCode === moduleCode);
    const hasSavedOverride = hasOverrideValue(row?.dashboardUrlOverride);
    const hasDraftOverride = hasOverrideValue(overrideDrafts[moduleCode]);

    if (!hasSavedOverride && !hasDraftOverride)
    {
      return;
    }

    if (!hasSavedOverride)
    {
      setOverrideDrafts((prev) => ({
        ...prev,
        [moduleCode]: '',
      }));
      addToast(`Override local do módulo ${moduleCode} descartado.`, 'success');
      return;
    }

    if (!canConfigureExperience)
    {
      addToast(
        config?.isLicenseBypass
          ? 'Nenhum módulo ativo no catálogo para alterar overrides.'
          : 'Ative uma assinatura antes de alterar overrides de dashboard.',
        'error'
      );
      return;
    }

    setClearingModuleCode(moduleCode);
    try {
      await licenseService.deleteProductModuleConfig(customerId, moduleCode);
      addToast(
        `Módulo ${moduleCode} voltou a usar a URL do catálogo.`,
        'success'
      );
      await loadConfig();
    } catch (error: unknown) {
      addToast(getApiErrorMessage(error, 'Erro ao remover override de dashboard.'), 'error');
    } finally {
      setClearingModuleCode(null);
    }
  };

  if (loading) {
    return <div className="py-8 text-gray-600">Carregando licenciamento…</div>;
  }

  if (!config) {
    return <div className="py-8 text-red-600">Não foi possível carregar a configuração.</div>;
  }

  const experienceDisabled = !canConfigureExperience;

  return (
    <div className="space-y-8">
      <section className="border rounded-lg p-5 bg-gray-50">
        <h2 className="text-lg font-semibold mb-3">Licenciamento vigente</h2>
        {config.isLicenseBypass ? (
          <p className="text-gray-600 text-sm">
            Tenant owner (Baruk): licença completa do catálogo sem assinatura comercial vinculada.
            Módulos:{' '}
            {config.licensedModuleCodes.length > 0
              ? config.licensedModuleCodes.join(', ')
              : 'nenhum módulo ativo no catálogo'}.
          </p>
        ) : config.hasActiveSubscription ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-gray-500">Plano</dt>
              <dd className="font-medium">{config.planCode ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">ID da assinatura</dt>
              <dd className="font-mono text-xs break-all">{config.activeSubscriptionId ?? '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Módulos licenciados</dt>
              <dd className="font-medium">
                {config.licensedModuleCodes.length > 0
                  ? config.licensedModuleCodes.join(', ')
                  : 'Nenhum módulo licenciado'}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-gray-600 text-sm">
            Este cliente não possui assinatura ativa. Use o painel acima para criar e ativar uma
            assinatura antes de definir preferências de experiência multi-produto.
          </p>
        )}
      </section>

      {!canConfigureExperience && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="status"
        >
          Sem assinatura ativa: preferências globais e overrides de dashboard estão desabilitados
          até que uma assinatura seja ativada.
        </div>
      )}

      <section className="border rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-4">Experiência multi-produto</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-1">Módulo padrão pós-login</label>
            <select
              value={defaultProductModuleId}
              onChange={e => setDefaultProductModuleId(e.target.value)}
              className="w-full border rounded px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={experienceDisabled}
            >
              <option value="">Primeiro módulo licenciado</option>
              {config.licensedModules.map(row => (
                <option key={row.productModuleId} value={row.productModuleId}>
                  {row.moduleName} ({row.moduleCode})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {config.isLicenseBypass
                ? 'Módulos ativos do catálogo (tenant owner).'
                : 'Só módulos licenciados pelo plano vigente aparecem na lista.'}
            </p>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <input
              id="requiresSystemSelection"
              type="checkbox"
              checked={requiresSystemSelection}
              onChange={e => setRequiresSystemSelection(e.target.checked)}
              className="h-5 w-5 cursor-pointer disabled:cursor-not-allowed"
              disabled={experienceDisabled || config.licensedModules.length <= 1}
            />
            <label htmlFor="requiresSystemSelection" className="font-medium cursor-pointer">
              Exigir seleção de sistema quando houver mais de um módulo
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={savingSystem || experienceDisabled}
            onClick={handleSaveSystemConfig}
          >
            {savingSystem ? 'Salvando…' : 'Salvar preferências globais'}
          </Button>
        </div>
      </section>

      <section className="border rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-4">URLs de dashboard por módulo</h2>
        {config.licensedModules.length === 0 ? (
          <p className="text-sm text-gray-600">
            {canConfigureExperience
              ? 'Nenhum módulo licenciado para configurar overrides.'
              : 'Ative uma assinatura para listar os módulos licenciados e configurar overrides.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-2 pr-4">Módulo</th>
                  <th className="py-2 pr-4">URL do catálogo</th>
                  <th className="py-2 pr-4">Override (opcional)</th>
                  <th className="py-2">URL resolvida</th>
                  <th className="py-2 pl-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {config.licensedModules.map(row => {
                  const draftValue = overrideDrafts[row.moduleCode] ?? '';
                  const resolvedUrl = draftValue.trim()
                    ? draftValue.trim()
                    : row.defaultDashboardUrl;
                  const savedOverride = hasOverrideValue(row.dashboardUrlOverride);
                  const draftOverride = hasOverrideValue(draftValue);
                  const showClearOverride = savedOverride || draftOverride;
                  const rowBusy =
                    savingModuleCode === row.moduleCode
                    || clearingModuleCode === row.moduleCode;

                  return (
                    <tr key={row.moduleCode} className="border-b align-top">
                      <td className="py-3 pr-4 font-medium">
                        {row.moduleName}
                        <span className="block text-xs text-gray-500">{row.moduleCode}</span>
                        {savedOverride && (
                          <span className="inline-block mt-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                            Override ativo
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs">{row.defaultDashboardUrl}</td>
                      <td className="py-3 pr-4">
                        <input
                          type="text"
                          value={draftValue}
                          onChange={e =>
                            setOverrideDrafts(prev => ({
                              ...prev,
                              [row.moduleCode]: e.target.value,
                            }))
                          }
                          placeholder={row.defaultDashboardUrl}
                          disabled={experienceDisabled}
                          className="w-full min-w-[200px] border rounded px-2 py-1 font-mono text-xs disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="py-3 font-mono text-xs text-gray-700">{resolvedUrl}</td>
                      <td className="py-3 pl-4">
                        <div className="flex flex-col gap-2 min-w-[120px]">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            disabled={rowBusy || experienceDisabled}
                            onClick={() => void handleSaveModuleOverride(row.moduleCode)}
                          >
                            {savingModuleCode === row.moduleCode ? '…' : 'Salvar'}
                          </Button>
                          {showClearOverride && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={rowBusy || experienceDisabled}
                              onClick={() => void handleClearModuleOverride(row.moduleCode)}
                            >
                              {clearingModuleCode === row.moduleCode ? '…' : 'Usar catálogo'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
